import { CAMPAIGN_DISCOUNT_METHOD } from '@Constants';
import { ensureArray, floatToPercentage, formatCurrencyFn, isNumber, toNumber } from '@Util/utils';

// 折扣顯示文字：百分比 0.9 為「9 折」、0.85 為「85 折」，現金為「折抵 5,000 元」
export const formatDiscountText = ({ discountMethod, discountValue }) => {
    if (discountMethod & CAMPAIGN_DISCOUNT_METHOD.PERCENT) {
        const rate = floatToPercentage(discountValue, { fixed: 0 });
        return `${rate % 10 === 0 ? rate / 10 : rate} 折`;
    }
    return `折抵 ${formatCurrencyFn(discountValue)} 元`;
};

// 名單項目種類：單品
export const REFERENCE_KIND_PRODUCT = 'product';
// 名單項目種類：前端分類
export const REFERENCE_KIND_CATEGORY = 'saleCategory';

// 基準價說明：決定折扣以哪一種價格計算
export const DISCOUNT_TYPE_TIP = '輸入價：門市結帳時指定的單價；原價：商品原價；售價：商品目前售價。百分比折扣以此價格乘折數，現金折抵每件最多折到此價格。';

// 抵用券類型說明：只影響白名單預設帶入的分類
export const CAMPAIGN_TYPE_HINT = '選擇後會自動帶入白名單的預設分類';

// 開啟商品限制但名單全空時的提示
export const LIMIT_ITEMS_REQUIRED_MESSAGE = '開啟商品限制時，白名單或黑名單至少要加入一個項目';

// 搜尋商品的最少字數
export const PRODUCT_SEARCH_MIN_LENGTH = 3;

// 名單項目的唯一鍵（種類＋ID）
export const getItemKey = ({ referenceKind, referenceID }) => `${referenceKind}_${referenceID}`;

// 建立「分類 ID → 完整名稱路徑」對照，子分類可能同名，需顯示完整路徑
export const buildCategoryPathMap = categoryTrees => {
    const pathMap = new Map();
    const walk = (nodes, parentPath) =>
        ensureArray(nodes).forEach(({ saleCategoryID, saleCategoryName, children }) => {
            const path = [...parentPath, saleCategoryName];
            pathMap.set(saleCategoryID, path);
            walk(children, path);
        });
    walk(categoryTrees, []);
    return pathMap;
};

// 名單項目顯示名稱，已刪除者加註（已刪除）
export const getItemDisplayName = ({ displayName, isDeleted }) => `${displayName || ''}${isDeleted ? '（已刪除）' : ''}`;

// 組出類型對應的預設白名單，類型的值即根分類代碼，找不到根節點時為空
export const buildDefaultIncludeItems = (categoryTrees, campaignType) => {
    const root = ensureArray(categoryTrees).find(({ saleCategoryID }) => String(saleCategoryID) === String(campaignType));
    if (!root) return [];
    return [{ referenceKind: REFERENCE_KIND_CATEGORY, referenceID: root.saleCategoryID, displayName: root.saleCategoryName, isDeleted: false }];
};

// 白名單項目是否為某個抵用券類型的根分類
const isTypeRootItem = (campaignTypes, item) =>
    item.referenceKind === REFERENCE_KIND_CATEGORY && ensureArray(campaignTypes).some(({ value }) => String(item.referenceID) === String(value));

// 依白名單推回抵用券類型：只含一種類型的根分類才回傳該類型的值，推不出來時回傳空字串
export const getCampaignTypeValue = (campaignTypes, includeItems) => {
    const matched = ensureArray(campaignTypes).filter(({ value }) => includeItems.some(item => item.referenceKind === REFERENCE_KIND_CATEGORY && String(item.referenceID) === String(value)));
    return matched.length === 1 ? String(matched[0].value) : '';
};

// 白名單是否仍為預設狀態：空的，或只有某個類型的根分類；預設狀態切換類型時可直接取代
export const isDefaultIncludeItems = (campaignTypes, includeItems) =>
    includeItems.length === 0 || (includeItems.length === 1 && isTypeRootItem(campaignTypes, includeItems[0]));

// 折抵數值上限，對齊資料庫 decimal(10,2)
const DISCOUNT_VALUE_MAX = 99999999.99;

// 驗證折抵數值，回傳錯誤訊息，通過時回傳空字串
export const getDiscountValueError = (discountMethod, discountValue) => {
    const text = String(discountValue ?? '').trim();
    if (!text) return '請輸入折抵數值';
    if (!isNumber(text)) return '折抵數值必須是數字';
    const value = toNumber(text);
    if (/\.\d{3,}$/.test(text)) return '折抵數值最多 2 位小數';
    if (Number(discountMethod) === CAMPAIGN_DISCOUNT_METHOD.PERCENT) return value > 0 && value < 1 ? '' : '百分比折扣須大於 0 且小於 1（0.85 代表 85 折）';
    if (value > DISCOUNT_VALUE_MAX) return '現金折抵最多 99,999,999.99 元';
    return value > 0 ? '' : '現金折抵須大於 0';
};

// 名單項目轉成送出內容，只留種類與 ID
const toRequestItems = items => items.map(({ referenceKind, referenceID }) => ({ referenceKind, referenceID }));

// 組出新增與修改共用的送出內容，開啟商品限制時折扣方式加 8 並帶入名單
export const buildCampaignPayload = ({ campaignName, sourceType, discountMethod, discountValue, discountType, startDate, endDate, isLimited, includeItems, excludeItems }) => {
    const payload = {
        campaignName: campaignName.trim(),
        sourceType: Number(sourceType),
        discountType: Number(discountType),
        discountMethod: Number(discountMethod) + (isLimited ? CAMPAIGN_DISCOUNT_METHOD.PRODUCT_LIMIT : 0),
        discountValue: Number(discountValue),
        startDate,
        endDate,
    };
    if (isLimited) {
        payload.includeItems = toRequestItems(includeItems);
        payload.excludeItems = toRequestItems(excludeItems);
    }
    return payload;
};
